/**
 * polygonCentroid — a representative interior point (≈ centroid) for a GeoJSON
 * Polygon or MultiPolygon. Pure + framework-free (no Cesium), so it's unit-testable
 * and reused by every layer that must anchor a centroid marker (evac-order disc
 * glyphs, the CAP kind-glyph). Extracted (icon one-swing #564) so the CAP layer and
 * the two evac-order layers share ONE centroid algorithm instead of re-deriving it.
 *
 * Algorithm: pick the OUTER ring with the most vertices (a cheap "largest area"
 * proxy across a MultiPolygon's parts) and return that ring's polygon centroid
 * (signed-area formula; a degenerate/zero-area ring falls back to the vertex mean).
 * The centroid can land just outside a strongly-concave area — acceptable for an
 * anchor glyph (warning/evac areas are blob-like). Returns null for empty geometry.
 */

type Ring = ReadonlyArray<readonly [number, number] | readonly number[]>;

/** Centroid of a single closed ring; degenerate (near-zero area) → vertex mean. */
function ringCentroid(ring: Ring): [number, number] | null {
  if (ring.length === 0) return null;
  let area = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const a = ring[i]!;
    const b = ring[i + 1]!;
    const x0 = a[0]!;
    const y0 = a[1]!;
    const x1 = b[0]!;
    const y1 = b[1]!;
    const cross = x0 * y1 - x1 * y0;
    area += cross;
    cx += (x0 + x1) * cross;
    cy += (y0 + y1) * cross;
  }
  area *= 0.5;
  if (Math.abs(area) < 1e-12) {
    let sx = 0;
    let sy = 0;
    for (const p of ring) {
      sx += p[0]!;
      sy += p[1]!;
    }
    return [sx / ring.length, sy / ring.length];
  }
  return [cx / (6 * area), cy / (6 * area)];
}

/** The GeoJSON geometry shapes polygonCentroid accepts (Polygon | MultiPolygon). */
interface PolygonLike {
  type?: string;
  coordinates?: unknown;
}

/**
 * A representative interior point for a GeoJSON Polygon or MultiPolygon. Returns
 * `[lon, lat]`, or null for empty/degenerate/non-polygonal geometry.
 */
export function polygonCentroid(geometry: PolygonLike | null | undefined): [number, number] | null {
  const coords = geometry?.coordinates;
  if (!Array.isArray(coords) || coords.length === 0) return null;
  // Normalize to a list of OUTER rings across the geometry's parts:
  //   Polygon.coordinates      = Ring[]            → outer ring = coords[0]
  //   MultiPolygon.coordinates = Array<Ring[]>     → outer rings = coords.map(p => p[0])
  const outerRings: Ring[] = [];
  const isMultiPolygon =
    geometry?.type === 'MultiPolygon' ||
    // structural fallback: coords[0][0] is itself an array of coordinate pairs.
    (Array.isArray(coords[0]) && Array.isArray((coords[0] as unknown[])[0]) &&
      Array.isArray(((coords[0] as unknown[])[0] as unknown[])[0]));
  if (isMultiPolygon) {
    for (const poly of coords as unknown[]) {
      const outer = Array.isArray(poly) ? (poly[0] as Ring | undefined) : undefined;
      if (outer && outer.length > 0) outerRings.push(outer);
    }
  } else {
    const outer = coords[0] as Ring | undefined;
    if (outer && outer.length > 0) outerRings.push(outer);
  }
  if (outerRings.length === 0) return null;
  // Largest-area proxy: the outer ring with the most vertices.
  let best: Ring | null = null;
  let bestLen = -1;
  for (const ring of outerRings) {
    if (ring.length > bestLen) {
      bestLen = ring.length;
      best = ring;
    }
  }
  return best ? ringCentroid(best) : null;
}
